import { useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo } from 'react';
import { filter, fromEvent, map, merge, switchMap, tap } from 'rxjs';
import { Vector3 } from 'three';

import { useToolPlayControllerContext } from '@/component/Tool/ToolPlayController';
import { pcdManager } from '@/manager';
import {
  new__cn as cn,
  useFlag,
  useSubscriptions,
  useUnleash,
  withTarget,
} from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  usePcdRenderingStore,
  useToolMainCameraStore,
  useToolSettingStore,
} from '@/store';

const BOX_TRACK_ID_EL = (() => {
  const el = document.createElement('div');

  el.className = cn(
    'absolute flex h-5 w-(--width) -translate-x-(--tx) -translate-y-2.5 flex-col items-center justify-center rounded-sm border border-gray-200 bg-bg-dark px-0.5 text-body-sm text-gray-200 select-none',
  );

  el.style.setProperty('--width', '28px');
  el.style.setProperty('--tx', 'calc(var(--width) / 2)');
  el.dataset.trackid = '-1';
  el.ariaLabel = 'box-track-id';

  return el;
})();

const ToolPcdCanvasBoxTrackID = () => {
  const { isPlaying } = useToolPlayControllerContext();
  const modeIndex = useToolMainCameraStore(state => state.modeIndex);
  const pcdIndex = usePcdRenderingStore(state => state.pcdIndex);
  const labelBoxTrackIDVisible = useToolSettingStore(
    state => state.labelBoxTrackIDVisible,
  );
  const { value: visible, setFalse: hide, setTrue: show } = useFlag(true);
  const mainCameraViewMode = useMemo(
    () => useToolMainCameraStore.getState().getMode(),
    [modeIndex],
  );

  useSubscriptions(
    () => [
      fromEvent(pcdManager, 'beforeChangeGeometry').pipe(tap(hide)).subscribe(),
      fromEvent(pcdManager, 'afterChangeGeometry').pipe(tap(show)).subscribe(),
    ],
    [pcdIndex],
  );

  return (
    mainCameraViewMode === 'top' &&
    !isPlaying &&
    labelBoxTrackIDVisible &&
    visible && <InnerToolPcdCanvasBoxTrackID />
  );
};

const InnerToolPcdCanvasBoxTrackID = () => {
  const isShortTrackID = useToolSettingStore(state => state.isShortTrackID);
  const { labelingBoxList, selectedBoxIdMap } = useLabelingBoxContext();
  const { gl, camera } = useThree();
  const canvasParentElement = gl.domElement.parentElement!;
  const { isEnabled } = useUnleash();
  const isTrackIdFlagOn = isEnabled('vuex.sapporo.trackId');

  const neededDataList = useMemo(
    () =>
      labelingBoxList.map((item, index) => ({
        dimension: item.dimension,
        index,
        position: item.position,
        selected: item.id ? selectedBoxIdMap[item.id] : false,
        trackId: item.trackId,
      })),
    [labelingBoxList, selectedBoxIdMap],
  );

  const setTrackIDInBoxTrackIDEl = (
    trackID: string,
    el: HTMLDivElement,
    isShortTrackID?: boolean,
  ) => {
    el.textContent =
      trackID.length > 4
        ? isShortTrackID
          ? trackID.slice(-4).replace(/^0+/, '')
          : trackID
        : trackID;
    el.style.setProperty(
      '--width',
      `${Math.max(Math.ceil(el.textContent.length * 6.76), 20)}px`,
    );
  };

  const renderBoxTrackID = useCallback(() => {
    // eslint-disable-next-line react-hooks/immutability
    removeBoxIndex();

    canvasParentElement.append(
      ...neededDataList
        .map(item => {
          const ndc = new Vector3(...item.position).project(camera);

          const w = gl.domElement.clientWidth;
          const h = gl.domElement.clientHeight;

          const x = ((ndc.x + 1) / 2) * w;
          const y = ((-ndc.y + 1) / 2) * h;

          const trackID = `${
            isTrackIdFlagOn ? item.trackId || item.index : item.index
          }`;

          return {
            x,
            y,
            selected: item.selected,
            trackID,
          };
        })
        .filter(
          ({ x, y }) =>
            x >= 0 &&
            y >= 0 &&
            x <= gl.domElement.clientWidth &&
            y <= gl.domElement.clientHeight,
        )
        .map(({ x, y, selected, trackID }) => {
          const boxTrackIDEl = BOX_TRACK_ID_EL.cloneNode(
            true,
          ) as HTMLDivElement;

          boxTrackIDEl.dataset.trackid = trackID;
          boxTrackIDEl.ariaLabel = `box-track-id-${trackID}`;

          boxTrackIDEl.style.top = `${y}px`;
          boxTrackIDEl.style.left = `${x}px`;

          setTrackIDInBoxTrackIDEl(trackID, boxTrackIDEl, isShortTrackID);

          if (selected) boxTrackIDEl.style.zIndex = '2';

          return boxTrackIDEl;
        }),
    );
  }, [camera, gl, isShortTrackID, neededDataList]);

  const removeBoxIndex = useCallback(() => {
    canvasParentElement
      .querySelectorAll('div[data-trackid]')
      .forEach(el => el.remove());
  }, [canvasParentElement]);

  useSubscriptions(
    () => [
      merge(
        fromEvent(window, 'mousemove').pipe(
          filter(
            e =>
              e.target === canvasParentElement ||
              canvasParentElement.contains(e.target as HTMLElement),
          ),
        ),
        fromEvent(window, 'click').pipe(
          filter(
            e =>
              e.target === canvasParentElement ||
              canvasParentElement.contains(e.target as HTMLElement),
          ),
        ),
        fromEvent(window, 'wheel').pipe(
          filter(
            e =>
              e.target === canvasParentElement ||
              canvasParentElement.contains(e.target as HTMLElement),
          ),
        ),
      )
        .pipe(tap(renderBoxTrackID))
        .subscribe(),

      fromEvent<MouseEvent>(window, 'mouseover')
        .pipe(
          withTarget,
          filter(
            e => isShortTrackID && !!e.target.ariaLabel?.match(/box-track-id/),
          ),
          map(e => e.target as HTMLDivElement),
          filter(
            target =>
              !!target.dataset.trackid && target.dataset.trackid.length > 4,
          ),
          switchMap(target => {
            const trackID = target.dataset.trackid!;

            setTrackIDInBoxTrackIDEl(trackID, target, false);

            return fromEvent<MouseEvent>(window, 'mouseout').pipe(
              tap(() =>
                setTrackIDInBoxTrackIDEl(trackID, target, isShortTrackID),
              ),
            );
          }),
        )
        .subscribe(),
    ],
    [camera, gl, isShortTrackID, neededDataList],
  );

  useEffect(() => {
    if (camera.isPerspectiveCamera) removeBoxIndex();
    else setTimeout(renderBoxTrackID, 50);
  }, [camera]);

  useEffect(() => {
    renderBoxTrackID();

    return removeBoxIndex;
  }, [isShortTrackID, neededDataList]);

  return <></>;
};

ToolPcdCanvasBoxTrackID.displayName = 'ToolPcdCanvasBoxTrackID';

export default ToolPcdCanvasBoxTrackID;
